<template>
  <div
    ref="toast"
    class="app-toast"
    :class="toastClasses"
    @transitionend="transitionEnded"
  >
    <div
      v-if="message.icon"
      class="toast-icon"
    >
      <app-icon :name="message.icon" />
      <span>{{ messageType }}</span>
    </div>
    <div
      v-else
      class="title"
    >
      <ic-warning v-if="message.isError" />
      <icn-infos v-else />
      <span>{{ messageType }}</span>
    </div>

    <div class="toast-content">
      <slot>
        <div
          class="toast-body"
          v-html="message.body"
        />
        <div
          v-if="message.errorCode"
          class="toast-body code"
        >
          Code: "{{ message.errorCode }}"
        </div>
      </slot>
      <div class="toast-time">
        {{ $t('date.ago', { time: since }) }}
      </div>
    </div>
    <div
      class="close-btn"
      @click="close"
    />
  </div>
</template>

<script>
import distanceInWordsToNow from 'date-fns/distance_in_words_to_now'
import frLocale from 'date-fns/locale/fr'
import appIcon from './app-icon'
import icWarning from '@/svg/ic-warning'
import icnInfos from '@/svg/icn-infos'

export default {
  name: 'AppToast',

  components: {
    appIcon,
    icWarning,
    icnInfos
  },
  props: {
    message: Object
  },

  data() {
    return {
      state: 'initial'
    }
  },

  computed: {
    toastClasses() {
      const toastClasses = [this.state]
      if (this.message.isError) toastClasses.push('error')
      return toastClasses
    },

    messageType() {
      return this.message.isError ? this.$t('toast.type.error') : this.$t('toast.type.information')
    },

    since({ message }) {
      return distanceInWordsToNow(message.timestamp, {
        locale: frLocale
      })
    }
  },

  mounted() {
    const { timeout } = this.message
    requestAnimationFrame(() => {
      this.state = 'opened'
    })

    if (timeout) {
      setTimeout(() => {
        this.state = 'closed'
      }, timeout)
    }
  },

  methods: {
    close() {
      this.state = 'closed'
    },

    transitionEnded({ target }) {
      const { state } = this
      if (state === 'closed' && target === this.$refs.toast) {
        this.$store.dispatch('toasts/removeMessage', {
          id: this.message.id
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped rel="stylesheet/scss">
@import '~@/styles/vars.scss';

.app-toast {
  position: relative;
  pointer-events: auto;
  display: flex;
  flex-wrap: wrap;
  width: 30rem;
  min-height: 10rem;
  padding: 1.3rem;
  border-radius: 4px;
  background-color: white;
  box-shadow: 0px 1px 6px 0 rgba(0, 0, 0, 0.15);
  transition: opacity 400ms;
  opacity: 0;

  &.opened {
    opacity: 1;
  }

  &.error {
    box-shadow: 0px 1px 7px 1px rgba(110, 0, 0, 0.25);
  }
}

.toast-icon {
  display: flex;
  align-items: center;
  border-right: 1px solid $color-diamond;
  padding-right: 1.6rem;

  .app-icon {
    width: 3.2rem;
    height: auto;
  }
}

.title {
  width: 100%;
  display: flex;
  align-items: center;

  svg {
    width: 26px;
    margin-right: 10px;
  }
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 1.8rem;
  font-weight: 600;
  letter-spacing: -0.4px;
}

.toast-content {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  padding-right: 10px;
  margin-top: 5px;
  // Upper SVG width and right margin
  margin-left: calc(26px + 10px);
}

.toast-body {
  font-size: 1.4rem;
  font-weight: 400;
  line-height: 1.57;

  &.code {
    color: #888;
    font-style: italic;
    font-size: 1.3rem;
  }
}

.toast-time {
  margin-top: 1.5rem;
  margin-bottom: 0;
  font-size: 1.2rem;
  color: $color-daddy;
  text-align: right;
  margin-right: 6px;
}

.close-btn {
  cursor: pointer;
  position: absolute;
  top: 1.2rem;
  right: 0.8rem;
  width: 1rem;
  height: 1rem;
  background-image: url('~@/assets/icn-close-gray-medium.svg');
  background-position: center center;
  background-repeat: no-repeat;
  background-size: contain;
}
</style>
